import { useEffect, useRef } from "react";

type Shape = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  sides: number;
  rotation: number;
  spin: number;
  color: string;
};

const palette = ["130,203,208", "243,107,53", "157,188,244", "255,214,111"];

export function FloatingShapes({ className = "" }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const context = canvas?.getContext("2d");
    if (!canvas || !host || !context) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let frame = 0;
    let shapes: Shape[] = [];
    let visible = true;
    const pointer = { x: -999, y: -999, active: false };
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const seedShapes = () => {
      shapes = Array.from({ length: width < 700 ? 8 : 14 }, (_, index) => ({
        x: ((index * 137.5) % 100) / 100 * width,
        y: ((index * 61.8 + 17) % 100) / 100 * height,
        vx: Math.sin(index * 2.1) * 0.045,
        vy: Math.cos(index * 1.7) * 0.038,
        size: 44 + ((index * 23) % 82),
        sides: [0, 4, 6, 3][index % 4],
        rotation: index * 0.7,
        spin: (index % 2 ? 1 : -1) * 0.0015,
        color: palette[index % palette.length],
      }));
    };

    const resize = () => {
      const rect = host.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedShapes();
    };

    const pointerMove = (event: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.active = true;
    };
    const pointerLeave = () => {
      pointer.active = false;
    };

    const polygon = (shape: Shape) => {
      context.beginPath();
      if (shape.sides === 0) {
        context.arc(shape.x, shape.y, shape.size * 0.5, 0, Math.PI * 2);
        return;
      }
      for (let side = 0; side < shape.sides; side += 1) {
        const angle = shape.rotation + (side / shape.sides) * Math.PI * 2;
        const x = shape.x + Math.cos(angle) * shape.size * 0.5;
        const y = shape.y + Math.sin(angle) * shape.size * 0.5;
        if (side === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.closePath();
    };

    const draw = () => {
      context.clearRect(0, 0, width, height);
      shapes.forEach((shape) => {
        if (!reduced) {
          shape.x += shape.vx;
          shape.y += shape.vy;
          shape.rotation += shape.spin;
        }

        if (pointer.active) {
          const dx = shape.x - pointer.x;
          const dy = shape.y - pointer.y;
          const distance = Math.sqrt(dx * dx + dy * dy) || 1;
          if (distance < 220) {
            const force = Math.pow(1 - distance / 220, 2) * 0.16;
            shape.vx += (dx / distance) * force;
            shape.vy += (dy / distance) * force;
          }
        }

        shape.vx *= 0.94;
        shape.vy *= 0.94;
        shape.vx += Math.sin(shape.y * 0.009) * 0.0012;
        shape.vy += Math.cos(shape.x * 0.008) * 0.0012;
        shape.vx = Math.max(-0.42, Math.min(0.42, shape.vx));
        shape.vy = Math.max(-0.42, Math.min(0.42, shape.vy));

        const margin = shape.size * 0.55;
        if (shape.x < margin) {
          shape.x = margin;
          shape.vx = Math.abs(shape.vx) * 0.7;
        }
        if (shape.x > width - margin) {
          shape.x = width - margin;
          shape.vx = -Math.abs(shape.vx) * 0.7;
        }
        if (shape.y < margin) {
          shape.y = margin;
          shape.vy = Math.abs(shape.vy) * 0.7;
        }
        if (shape.y > height - margin) {
          shape.y = height - margin;
          shape.vy = -Math.abs(shape.vy) * 0.7;
        }

        polygon(shape);
        context.fillStyle = `rgba(${shape.color}, 0.07)`;
        context.strokeStyle = `rgba(${shape.color}, 0.3)`;
        context.lineWidth = 1.35;
        context.fill();
        context.stroke();
      });

      if (!reduced && visible) frame = requestAnimationFrame(draw);
    };

    resize();
    draw();
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !reduced) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(draw);
      }
    });
    const resizeObserver = new ResizeObserver(resize);
    observer.observe(host);
    resizeObserver.observe(host);
    host.addEventListener("pointermove", pointerMove, { passive: true });
    host.addEventListener("pointerleave", pointerLeave);

    return () => {
      visible = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      host.removeEventListener("pointermove", pointerMove);
      host.removeEventListener("pointerleave", pointerLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className={`floating-shapes ${className}`.trim()} aria-hidden="true" />;
}
